import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';

export type RepertoirePlaybackMode = 'practice' | 'listen';

export const DEFAULT_REPERTOIRE_PLAYBACK_MODE: RepertoirePlaybackMode = 'practice';

export const REPERTOIRE_PLAYBACK_MODE_OPTIONS: Array<{
  value: RepertoirePlaybackMode;
  label: string;
  shortLabel: string;
  description: string;
}> = [
  {
    value: 'practice',
    label: 'Accuracy Scoring On',
    shortLabel: 'Scoring On',
    description: 'Recognition and feedback enabled.',
  },
  {
    value: 'listen',
    label: 'Score Playback Only',
    shortLabel: 'Sound Only',
    description: 'Sound only. No error recognition.',
  },
];

const REPERTOIRE_PLAYBACK_MODE_STORAGE_KEY = 'museflow_repertoire_playback_mode';

export const isRepertoirePlaybackModesEnabled = () => {
  if (typeof window === 'undefined') return false;
  return isFeatureEnabled(FeatureFlag.USE_REPERTOIRE_PLAYBACK_MODES);
};

export const coerceRepertoirePlaybackModeForFeatureFlag = (
  mode: RepertoirePlaybackMode,
): RepertoirePlaybackMode =>
  isRepertoirePlaybackModesEnabled() ? mode : DEFAULT_REPERTOIRE_PLAYBACK_MODE;

export const repertoirePlaybackModePlaysNotes = (mode: RepertoirePlaybackMode) => mode === 'listen';

export const repertoirePlaybackModeDisablesRecognition = (mode: RepertoirePlaybackMode) =>
  mode === 'listen';

export const readStoredRepertoirePlaybackMode = (): RepertoirePlaybackMode => {
  if (typeof window === 'undefined') return DEFAULT_REPERTOIRE_PLAYBACK_MODE;
  if (!isRepertoirePlaybackModesEnabled()) {
    try {
      window.localStorage.setItem(
        REPERTOIRE_PLAYBACK_MODE_STORAGE_KEY,
        DEFAULT_REPERTOIRE_PLAYBACK_MODE,
      );
    } catch {
      // Ignore storage failures. The in-memory default still applies for this session.
    }

    return DEFAULT_REPERTOIRE_PLAYBACK_MODE;
  }

  const storedMode = window.localStorage.getItem(REPERTOIRE_PLAYBACK_MODE_STORAGE_KEY);
  if (storedMode === 'practice' || storedMode === 'listen') {
    return storedMode;
  }

  return DEFAULT_REPERTOIRE_PLAYBACK_MODE;
};

export const storeRepertoirePlaybackMode = (mode: RepertoirePlaybackMode) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      REPERTOIRE_PLAYBACK_MODE_STORAGE_KEY,
      coerceRepertoirePlaybackModeForFeatureFlag(mode),
    );
  } catch {
    // Ignore storage failures. The in-memory mode still applies for this session.
  }
};
